import React, { useState, useEffect, useCallback } from 'react';
import { 
  ShoppingCart, Plus, Search, Tag, Percent, ExternalLink, Trash2, Edit, 
  Check, X, DollarSign, TrendingDown, Bell, ListChecks, Store, Sparkles,
  ChevronDown, Copy, Clock, AlertCircle, ShoppingBag, BarChart3, Loader2,
  Radar, Target, Save, RefreshCw, Eye, Share2
} from 'lucide-react';
import { toast } from 'sonner';
import { useUserId } from '../context/UserAuthContext';

// Use runtime origin for API calls to ensure correct URL
const API_URL = typeof window !== 'undefined' ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || '');

const Shopping = () => {
  const userId = useUserId();
  
  // Main state
  const [stores, setStores] = useState([]);
  const [deals, setDeals] = useState([]);
  const [shoppingLists, setShoppingLists] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [priceAlerts, setPriceAlerts] = useState([]);
  const [aiDeals, setAiDeals] = useState([]);
  
  // Scraper state
  const [scrapedDeals, setScrapedDeals] = useState([]);
  const [trackedProducts, setTrackedProducts] = useState([]);
  const [dealNotifications, setDealNotifications] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [scraperStores, setScraperStores] = useState([]);
  const [scraperSearchQuery, setScraperSearchQuery] = useState('');
  const [showTrackModal, setShowTrackModal] = useState(false);
  const [newTrack, setNewTrack] = useState({ product_name: '', target_price: '', stores: [], keywords: '' });
  
  // UI state
  const [activeTab, setActiveTab] = useState('all'); // 'all' or store id
  const [activeSection, setActiveSection] = useState('lists'); // 'lists', 'deals', 'scraper', 'coupons', 'ai'
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal state
  const [showAddDealModal, setShowAddDealModal] = useState(false);
  const [showAddListModal, setShowAddListModal] = useState(false);
  const [showAddCouponModal, setShowAddCouponModal] = useState(false);
  const [showAddStoreModal, setShowAddStoreModal] = useState(false);
  const [showListItemsModal, setShowListItemsModal] = useState(null); // stores list ID only
  const [editingItem, setEditingItem] = useState(null);
  
  // Form state
  const [newDeal, setNewDeal] = useState({ store_id: '', product_name: '', original_price: '', sale_price: '', product_url: '', image_url: '', description: '', expires_at: '', category: '' });
  const [newList, setNewList] = useState({ name: '', store_id: '', budget: '', is_advanced: false });
  const [newCoupon, setNewCoupon] = useState({ store_id: '', code: '', description: '', discount_type: 'percentage', discount_value: '', min_purchase: '', expires_at: '' });
  const [newStore, setNewStore] = useState({ name: '', website_url: '', color: '#8B5CF6' });
  const [newListItem, setNewListItem] = useState({ name: '', quantity: 1, price: '', product_url: '', notes: '' });
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingItemData, setEditingItemData] = useState({});

  // Derive active list live from shoppingLists so modal always shows fresh data
  const activeList = showListItemsModal ? shoppingLists.find(l => l.id === showListItemsModal) : null;

  // Fetch all data
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    let storesLoaded = false;
    
    try {
      // Always fetch stores (they have defaults)
      const storesRes = await fetch(`${API_URL}/api/shopping/stores?user_id=${encodeURIComponent(userId || 'guest')}`);
      if (storesRes.ok) {
        const data = await storesRes.json();
        setStores(data.stores || []);
        storesLoaded = true;
      }
      
      // Only fetch user data if logged in
      if (userId) {
        try {
          const [dealsRes, listsRes, couponsRes, alertsRes] = await Promise.all([
            fetch(`${API_URL}/api/shopping/deals?user_id=${encodeURIComponent(userId)}`),
            fetch(`${API_URL}/api/shopping/lists?user_id=${encodeURIComponent(userId)}`),
            fetch(`${API_URL}/api/shopping/coupons?user_id=${encodeURIComponent(userId)}`),
            fetch(`${API_URL}/api/shopping/price-alerts?user_id=${encodeURIComponent(userId)}`)
          ]);
          
          if (dealsRes.ok) {
            const data = await dealsRes.json();
            setDeals(data.deals || []);
          }
          if (listsRes.ok) {
            const data = await listsRes.json();
            setShoppingLists(data.lists || []);
          }
          if (couponsRes.ok) {
            const data = await couponsRes.json();
            setCoupons(data.coupons || []);
          }
          if (alertsRes.ok) {
            const data = await alertsRes.json();
            setPriceAlerts(data.alerts || []);
          }
        } catch (userDataError) {
          console.error('Failed to fetch user shopping data:', userDataError);
        }
      }
    } catch (error) {
      console.error('Failed to fetch shopping stores:', error);
      if (!storesLoaded) {
        toast.error('Failed to load stores');
      }
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Find AI deals
  const findAiDeals = async () => {
    if (!userId) return;
    
    try {
      const storeParam = activeTab !== 'all' ? `&store_id=${activeTab}` : '';
      const response = await fetch(`${API_URL}/api/shopping/ai/find-deals?user_id=${encodeURIComponent(userId)}${storeParam}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        setAiDeals(data.deals || []);
        setActiveSection('ai');
        toast.success(`Found ${data.deals?.length || 0} AI-powered deal suggestions!`);
      }
    } catch (error) {
      console.error('Failed to find AI deals:', error);
      toast.error('Failed to find deals');
    }
  };

  // Save AI deal
  const saveAiDeal = async (deal) => {
    if (!userId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/shopping/ai/save-deal?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(deal)
      });
      
      if (response.ok) {
        const data = await response.json();
        setDeals(prev => [data.deal, ...prev]);
        setAiDeals(prev => prev.filter(d => d.id !== deal.id));
        toast.success('Deal saved to your tracked deals!');
      }
    } catch (error) {
      console.error('Failed to save AI deal:', error);
      toast.error('Failed to save deal');
    }
  };

  // Add deal
  const handleAddDeal = async () => {
    if (!newDeal.product_name || !newDeal.sale_price || !newDeal.store_id) {
      toast.error('Product name, price, and store are required');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/shopping/deals?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newDeal,
          original_price: newDeal.original_price ? parseFloat(newDeal.original_price) : null,
          sale_price: parseFloat(newDeal.sale_price)
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setDeals(prev => [data.deal, ...prev]);
        setShowAddDealModal(false);
        setNewDeal({ store_id: '', product_name: '', original_price: '', sale_price: '', product_url: '', image_url: '', description: '', expires_at: '', category: '' });
        toast.success('Deal added!');
      }
    } catch (error) {
      console.error('Failed to add deal:', error);
      toast.error('Failed to add deal');
    }
  };

  // Delete deal
  const handleDeleteDeal = async (dealId) => {
    try {
      const response = await fetch(`${API_URL}/api/shopping/deals/${dealId}?user_id=${encodeURIComponent(userId)}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setDeals(prev => prev.filter(d => d.id !== dealId));
        toast.success('Deal removed');
      }
    } catch (error) {
      console.error('Failed to delete deal:', error);
      toast.error('Failed to delete deal');
    }
  };

  // Share shopping list
  const handleShareList = async (listId, e) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API_URL}/api/shopping/lists/${listId}/share?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST'
      });
      if (!res.ok) throw new Error('Share failed');
      const data = await res.json();
      const shareUrl = `${window.location.origin}/shared/shopping/${data.token}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied to clipboard!');
    } catch (err) {
      console.error('Share error:', err);
      toast.error('Failed to generate share link');
    }
  };

  // Add shopping list
  const handleAddList = async () => {
    if (!newList.name) {
      toast.error('List name is required');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/shopping/lists?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newList,
          budget: newList.budget ? parseFloat(newList.budget) : null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setShoppingLists(prev => [data.list, ...prev]);
        setShowAddListModal(false);
        setNewList({ name: '', store_id: '', budget: '', is_advanced: false });
        toast.success('Shopping list created!');
      }
    } catch (error) {
      console.error('Failed to add list:', error);
      toast.error('Failed to create list');
    }
  };

  // Delete shopping list
  const handleDeleteList = async (listId) => {
    try {
      const response = await fetch(`${API_URL}/api/shopping/lists/${listId}?user_id=${encodeURIComponent(userId)}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setShoppingLists(prev => prev.filter(l => l.id !== listId));
        toast.success('List deleted');
      }
    } catch (error) {
      console.error('Failed to delete list:', error);
      toast.error('Failed to delete list');
    }
  };

  // Add list item
  const handleAddListItem = async (listId) => {
    if (!newListItem.name) {
      toast.error('Item name is required');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/shopping/lists/${listId}/items?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          list_id: listId,
          name: newListItem.name,
          quantity: parseInt(newListItem.quantity) || 1,
          price: newListItem.price ? parseFloat(newListItem.price) : null,
          product_url: newListItem.product_url || null,
          notes: newListItem.notes || null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setShoppingLists(prev => prev.map(l => l.id !== listId ? l : {
          ...l,
          items: [...(l.items || []), data.item],
          total_items: (l.total_items || 0) + 1,
          estimated_total: (l.estimated_total || 0) + ((data.item.price || 0) * (data.item.quantity || 1))
        }));
        setNewListItem({ name: '', quantity: 1, price: '', product_url: '', notes: '' });
        toast.success('Item added!');
      }
    } catch (error) {
      console.error('Failed to add item:', error);
      toast.error('Failed to add item');
    }
  };

  // Toggle item checked
  const handleToggleItem = async (listId, itemId, isChecked) => {
    try {
      await fetch(`${API_URL}/api/shopping/lists/${listId}/items/${itemId}?user_id=${encodeURIComponent(userId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_checked: !isChecked })
      });
      
      setShoppingLists(prev => prev.map(l => {
        if (l.id === listId) {
          return {
            ...l,
            items: l.items.map(i => i.id === itemId ? { ...i, is_checked: !isChecked } : i),
            checked_items: l.checked_items + (isChecked ? -1 : 1)
          };
        }
        return l;
      }));
    } catch (error) {
      console.error('Failed to toggle item:', error);
    }
  };

  // Delete list item
  const handleDeleteListItem = async (listId, itemId) => {
    try {
      await fetch(`${API_URL}/api/shopping/lists/${listId}/items/${itemId}?user_id=${encodeURIComponent(userId)}`, {
        method: 'DELETE'
      });
      
      setShoppingLists(prev => prev.map(l => {
        if (l.id !== listId) return l;
        const item = l.items?.find(i => i.id === itemId);
        return {
          ...l,
          items: (l.items || []).filter(i => i.id !== itemId),
          total_items: (l.total_items || 1) - 1,
          checked_items: (l.checked_items || 0) - (item?.is_checked ? 1 : 0),
          estimated_total: (l.estimated_total || 0) - ((item?.price || 0) * (item?.quantity || 1))
        };
      }));
      toast.success('Item removed');
    } catch (error) {
      console.error('Failed to delete item:', error);
      toast.error('Failed to delete item');
    }
  };

  // Edit list item
  const handleEditListItem = async (listId, itemId) => {
    try {
      const response = await fetch(`${API_URL}/api/shopping/lists/${listId}/items/${itemId}?user_id=${encodeURIComponent(userId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editingItemData.name,
          quantity: parseInt(editingItemData.quantity) || 1,
          price: editingItemData.price ? parseFloat(editingItemData.price) : null,
          product_url: editingItemData.product_url || null,
          notes: editingItemData.notes || null
        })
      });
      if (response.ok) {
        const data = await response.json();
        setShoppingLists(prev => prev.map(l => l.id !== listId ? l : {
          ...l,
          items: (l.items || []).map(i => i.id === itemId ? data.item : i),
          estimated_total: (l.items || []).reduce((sum, i) => {
            const item = i.id === itemId ? data.item : i;
            return sum + ((item.price || 0) * (item.quantity || 1));
          }, 0)
        }));
        setEditingItemId(null);
        setEditingItemData({});
        toast.success('Item updated');
      }
    } catch (error) {
      console.error('Failed to update item:', error);
      toast.error('Failed to update item');
    }
  };

  // Add coupon
  const handleAddCoupon = async () => {
    if (!newCoupon.code || !newCoupon.store_id) {
      toast.error('Coupon code and store are required');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/shopping/coupons?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newCoupon,
          discount_value: newCoupon.discount_value ? parseFloat(newCoupon.discount_value) : null,
          min_purchase: newCoupon.min_purchase ? parseFloat(newCoupon.min_purchase) : null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setCoupons(prev => [data.coupon, ...prev]);
        setShowAddCouponModal(false);
        setNewCoupon({ store_id: '', code: '', description: '', discount_type: 'percentage', discount_value: '', min_purchase: '', expires_at: '' });
        toast.success('Coupon saved!');
      }
    } catch (error) {
      console.error('Failed to add coupon:', error);
      toast.error('Failed to save coupon');
    }
  };

  // Delete coupon
  const handleDeleteCoupon = async (couponId) => {
    try {
      await fetch(`${API_URL}/api/shopping/coupons/${couponId}?user_id=${encodeURIComponent(userId)}`, {
        method: 'DELETE'
      });
      
      setCoupons(prev => prev.filter(c => c.id !== couponId));
      toast.success('Coupon removed');
    } catch (error) {
      console.error('Failed to delete coupon:', error);
    }
  };

  // Copy coupon code
  const copyCouponCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Coupon code copied!');
  };

  // Add store
  const handleAddStore = async () => {
    if (!newStore.name) {
      toast.error('Store name is required');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/shopping/stores?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newStore,
          logo_url: `https://logo.clearbit.com/${newStore.website_url?.replace(/https?:\/\//, '').replace(/\/.*/g, '') || newStore.name.toLowerCase() + '.com'}`
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setStores(prev => [...prev, data.store]);
        setShowAddStoreModal(false);
        setNewStore({ name: '', website_url: '', color: '#8B5CF6' });
        toast.success('Store added!');
      }
    } catch (error) {
      console.error('Failed to add store:', error);
      toast.error('Failed to add store');
    }
  };

  // Filter deals by store and search
  const filteredDeals = deals.filter(deal => {
    const matchesStore = activeTab === 'all' || deal.store_id === activeTab;
    const matchesSearch = !searchTerm || 
      deal.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      deal.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStore && matchesSearch;
  });

  // Filter coupons by store
  const filteredCoupons = coupons.filter(coupon => {
    return activeTab === 'all' || coupon.store_id === activeTab;
  });

  // Get store by ID
  const getStore = (storeId) => stores.find(s => s.id === storeId) || {};

  // KPI calculations
  const totalSavings = deals.reduce((sum, d) => sum + ((d.original_price || 0) - (d.sale_price || 0)), 0);
  const activeDealsCount = deals.filter(d => d.is_active !== false).length;
  const totalListItems = shoppingLists.reduce((sum, l) => sum + (l.total_items || 0), 0);
  const activeCouponsCount = coupons.filter(c => c.is_active !== false).length;

  // ========== DEAL SCRAPER FUNCTIONS ==========
  
  // Fetch scraper stores
  const fetchScraperStores = async () => {
    try {
      const response = await fetch(`${API_URL}/api/deal-scraper/stores`);
      if (response.ok) {
        const data = await response.json();
        setScraperStores(data.stores || []);
      }
    } catch (error) {
      console.error('Failed to fetch scraper stores:', error);
    }
  };

  // Scan for deals across all stores
  const scanDeals = async () => {
    setIsScanning(true);
    const loadingToast = toast.loading('Scanning deals from Amazon, Walmart, Target, Best Buy...');
    
    try {
      const storeFilter = activeTab !== 'all' ? [activeTab] : [];
      const response = await fetch(`${API_URL}/api/deal-scraper/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stores: storeFilter,
          categories: [],
          search_query: scraperSearchQuery || null,
          max_results: 50
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setScrapedDeals(data.deals || []);
        setActiveSection('scraper');
        toast.success(`Found ${data.count || 0} deals!`, { id: loadingToast });
      } else {
        toast.error('Scan failed', { id: loadingToast });
      }
    } catch (error) {
      console.error('Deal scan error:', error);
      toast.error('Failed to scan deals', { id: loadingToast });
    } finally {
      setIsScanning(false);
    }
  };

  // Search for specific product deals
  const searchScrapedDeals = async () => {
    if (!scraperSearchQuery.trim()) {
      toast.error('Enter a product to search');
      return;
    }
    
    setIsScanning(true);
    const loadingToast = toast.loading(`Searching for "${scraperSearchQuery}"...`);
    
    try {
      const response = await fetch(`${API_URL}/api/deal-scraper/search?query=${encodeURIComponent(scraperSearchQuery)}&max_results=30`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        setScrapedDeals(data.deals || []);
        setActiveSection('scraper');
        toast.success(`Found ${data.count || 0} deals for "${scraperSearchQuery}"`, { id: loadingToast });
      } else {
        toast.error('Search failed', { id: loadingToast });
      }
    } catch (error) {
      console.error('Deal search error:', error);
      toast.error('Failed to search deals', { id: loadingToast });
    } finally {
      setIsScanning(false);
    }
  };

  // Save a scraped deal to user's collection
  const saveScrapedDeal = async (deal) => {
    try {
      const response = await fetch(`${API_URL}/api/deal-scraper/save-deal?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(deal)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Deal saved to My Deals!');
        
        // Mark as saved in scraped deals UI
        setScrapedDeals(prev => prev.map(d => 
          d.deal_id === deal.deal_id ? { ...d, saved: true } : d
        ));
        
        // Also add to My Deals list instantly
        const savedDeal = data.deal || {
          id: deal.deal_id || `saved_${Date.now()}`,
          store_id: deal.store || 'other',
          product_name: deal.product_name,
          original_price: null,
          sale_price: deal.sale_price,
          product_url: deal.product_url,
          image_url: deal.image_url,
          description: deal.description,
          category: deal.category,
          created_at: new Date().toISOString()
        };
        setDeals(prev => [savedDeal, ...prev.filter(d => d.id !== savedDeal.id)]);
      } else {
        const data = await response.json();
        toast.error(data.message || 'Failed to save');
      }
    } catch (error) {
      console.error('Save deal error:', error);
      toast.error('Failed to save deal');
    }
  };

  // Add deal to shopping list
  const addDealToList = async (deal, listId) => {
    if (!listId) {
      toast.error('Please select a shopping list');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/shopping/lists/${listId}/items?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: deal.product_name,
          quantity: 1,
          price: deal.sale_price,
          url: deal.product_url,
          image_url: deal.image_url,
          store: deal.store_name || deal.store,
          notes: deal.description?.substring(0, 200) || ''
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update local shopping lists state
        setShoppingLists(prev => prev.map(list => 
          list.id === listId 
            ? { ...list, items: [...(list.items || []), data.item] }
            : list
        ));
        toast.success('Added to shopping list!');
        setShowAddToListModal(null); // Close the modal
      } else {
        toast.error('Failed to add to list');
      }
    } catch (error) {
      console.error('Add to list error:', error);
      toast.error('Failed to add to list');
    }
  };

  // State for add to list modal
  const [showAddToListModal, setShowAddToListModal] = useState(null);
  const [selectedListId, setSelectedListId] = useState('');

  // Track a product for price alerts
  const handleTrackProduct = async () => {
    if (!newTrack.product_name.trim()) {
      toast.error('Product name is required');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/deal-scraper/track?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_name: newTrack.product_name,
          target_price: newTrack.target_price ? parseFloat(newTrack.target_price) : null,
          stores: newTrack.stores,
          keywords: newTrack.keywords ? newTrack.keywords.split(',').map(k => k.trim()) : [],
          notify_any_deal: true
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setTrackedProducts(prev => [...prev, data.track]);
        setShowTrackModal(false);
        setNewTrack({ product_name: '', target_price: '', stores: [], keywords: '' });
        toast.success(`Now tracking "${newTrack.product_name}"`);
      }
    } catch (error) {
      console.error('Track product error:', error);
      toast.error('Failed to track product');
    }
  };

  // Fetch tracked products
  const fetchTrackedProducts = async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${API_URL}/api/deal-scraper/tracks?user_id=${encodeURIComponent(userId)}`);
      if (response.ok) {
        const data = await response.json();
        setTrackedProducts(data.tracks || []);
      }
    } catch (error) {
      console.error('Failed to fetch tracked products:', error);
    }
  };

  // Delete tracked product
  const deleteTrackedProduct = async (trackId) => {
    try {
      const response = await fetch(`${API_URL}/api/deal-scraper/tracks/${trackId}?user_id=${encodeURIComponent(userId)}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setTrackedProducts(prev => prev.filter(t => t.id !== trackId));
        toast.success('Product tracking removed');
      }
    } catch (error) {
      console.error('Delete track error:', error);
      toast.error('Failed to remove tracking');
    }
  };

  // Check tracked products for matches
  const checkTrackedProducts = async () => {
    if (!userId) return;
    setIsScanning(true);
    const loadingToast = toast.loading('Checking tracked products for deals...');
    
    try {
      const response = await fetch(`${API_URL}/api/deal-scraper/check-tracks?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.matches?.length > 0) {
          setScrapedDeals(data.matches);
          setActiveSection('scraper');
          toast.success(`Found ${data.count} deals for your tracked products!`, { id: loadingToast });
        } else {
          toast.info('No new deals found for tracked products', { id: loadingToast });
        }
      }
    } catch (error) {
      console.error('Check tracks error:', error);
      toast.error('Failed to check tracked products', { id: loadingToast });
    } finally {
      setIsScanning(false);
    }
  };

  // Load scraper data on section change
  useEffect(() => {
    if (activeSection === 'scraper' && scraperStores.length === 0) {
      fetchScraperStores();
      fetchTrackedProducts();
    }
  }, [activeSection]);

  // ========== END DEAL SCRAPER FUNCTIONS ==========

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="min-h-screen bg-black text-white p-4 md:p-6">
        <div className="max-w-md mx-auto text-center py-20">
          <ShoppingCart size={64} className="mx-auto mb-6 text-purple-500 opacity-50" />
          <h1 className="text-2xl font-bold mb-4">Shopping Hub</h1>
          <p className="text-gray-400 mb-6">Please sign in to track deals, create shopping lists, and save coupons.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
            <ShoppingCart className="text-purple-500" />
            Shopping
          </h1>
          <p className="text-gray-400 text-sm mt-1">Track deals, manage lists, and save money</p>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={findAiDeals}
            data-testid="find-ai-deals-btn"
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-lg text-sm font-medium transition-all"
          >
            <Sparkles size={16} />
            Find AI Deals
          </button>
          <button
            onClick={() => setShowAddStoreModal(true)}
            data-testid="add-store-btn"
            className="p-2 bg-black border border-white/20 hover:border-white/40 rounded-lg transition-all"
            title="Add Store"
          >
            <Store size={18} />
          </button>
        </div>
      </div>

      {/* KPI Banner */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
        <div className="bg-black border border-white/40 rounded-xl p-4 hover:border-green-500/50 transition-all">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-400">Total Savings</span>
            <TrendingDown size={16} className="text-green-400" />
          </div>
          <div className="text-xl md:text-2xl font-bold text-green-400">${totalSavings.toFixed(2)}</div>
        </div>
        <div className="bg-black border border-white/40 rounded-xl p-4 hover:border-purple-500/50 transition-all">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-400">Active Deals</span>
            <Tag size={16} className="text-purple-400" />
          </div>
          <div className="text-xl md:text-2xl font-bold">{activeDealsCount}</div>
        </div>
        <div className="bg-black border border-white/40 rounded-xl p-4 hover:border-blue-500/50 transition-all">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-400">List Items</span>
            <ListChecks size={16} className="text-blue-400" />
          </div>
          <div className="text-xl md:text-2xl font-bold">{totalListItems}</div>
        </div>
        <div className="bg-black border border-white/40 rounded-xl p-4 hover:border-yellow-500/50 transition-all">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-400">Saved Coupons</span>
            <Percent size={16} className="text-yellow-400" />
          </div>
          <div className="text-xl md:text-2xl font-bold">{activeCouponsCount}</div>
        </div>
      </div>

      {/* Store Tabs */}
      <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
        <button
          onClick={() => setActiveTab('all')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
            activeTab === 'all' ? 'bg-purple-600 text-white' : 'bg-black border border-white/20 hover:border-white/40'
          }`}
        >
          All Stores
        </button>
        {stores.map((store) => (
          <button
            key={store.id}
            onClick={() => setActiveTab(store.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              activeTab === store.id ? 'text-white' : 'bg-black border border-white/20 hover:border-white/40'
            }`}
            style={activeTab === store.id ? { backgroundColor: store.color || '#8B5CF6' } : {}}
          >
            {store.logo_url ? (
              <img 
                src={store.logo_url} 
                alt={store.name} 
                className="w-5 h-5 rounded object-contain bg-white p-0.5"
                onError={(e) => { 
                  e.target.style.display = 'none';
                  e.target.nextSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <span 
              className={`w-5 h-5 rounded flex items-center justify-center text-xs font-bold text-white ${store.logo_url ? 'hidden' : ''}`}
              style={{ backgroundColor: store.color || '#8B5CF6' }}
            >
              {store.name?.charAt(0) || 'S'}
            </span>
            {store.name}
          </button>
        ))}
        {/* Add Store Button in tabs */}
        <button
          onClick={() => setShowAddStoreModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap bg-black border border-dashed border-white/30 hover:border-purple-500 text-gray-400 hover:text-purple-400 transition-all"
          data-testid="add-store-inline-btn"
        >
          <Plus size={16} />
          Add Store
        </button>
      </div>

      {/* Section Tabs */}
      <div className="flex items-center gap-2 mb-6 border-b border-white/10 pb-4 overflow-x-auto">
        {[
          { id: 'lists', label: 'Shopping Lists', icon: ListChecks },
          { id: 'scraper', label: 'Deal Scanner', icon: Radar },
          { id: 'deals', label: 'My Deals', icon: Tag },
          { id: 'coupons', label: 'Coupons', icon: Percent },
          { id: 'ai', label: 'AI Suggestions', icon: Sparkles }
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveSection(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              activeSection === id ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            <Icon size={16} />
            {label}
            {id === 'ai' && aiDeals.length > 0 && (
              <span className="bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full">{aiDeals.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Deal Scanner Section */}
      {activeSection === 'scraper' && (
        <div className="space-y-6">
          {/* Scanner Controls */}
          <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-xl p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
                  <Radar className="text-purple-400" />
                  Deal Scanner
                </h3>
                <p className="text-sm text-gray-400">
                  Scan deals from Amazon, Walmart, Target, Best Buy and more in real-time
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full md:w-auto">
                <div className="relative flex-1 sm:w-64">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={scraperSearchQuery}
                    onChange={(e) => setScraperSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchScrapedDeals()}
                    placeholder="Search product..."
                    className="w-full pl-10 pr-4 py-2 bg-black/50 border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                  />
                </div>
                <button
                  onClick={scraperSearchQuery ? searchScrapedDeals : scanDeals}
                  disabled={isScanning}
                  data-testid="scan-deals-btn"
                  className="flex items-center justify-center gap-2 px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-600/50 rounded-lg font-medium transition-all"
                >
                  {isScanning ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Radar size={18} />
                  )}
                  {isScanning ? 'Scanning...' : (scraperSearchQuery ? 'Search' : 'Scan All')}
                </button>
              </div>
            </div>
          </div>

          {/* Tracked Products & Quick Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Track New Product */}
            <div className="bg-black border border-white/20 rounded-xl p-4">
              <h4 className="font-semibold flex items-center gap-2 mb-3">
                <Target size={16} className="text-blue-400" />
                Track Products
              </h4>
              <p className="text-xs text-gray-400 mb-3">Get notified when prices drop</p>
              <button
                onClick={() => setShowTrackModal(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded-lg text-blue-400 text-sm transition-all"
              >
                <Plus size={16} />
                Track Product
              </button>
              {trackedProducts.length > 0 && (
                <div className="mt-3 space-y-2">
                  {trackedProducts.slice(0, 3).map((track) => (
                    <div key={track.id} className="flex items-center justify-between bg-white/5 rounded-lg p-2">
                      <span className="text-xs truncate flex-1">{track.product_name}</span>
                      {track.target_price && (
                        <span className="text-xs text-green-400 ml-2">${track.target_price}</span>
                      )}
                      <button
                        onClick={() => deleteTrackedProduct(track.id)}
                        className="ml-2 p-1 hover:bg-red-500/20 rounded text-red-400"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {trackedProducts.length > 3 && (
                    <p className="text-xs text-gray-500 text-center">+{trackedProducts.length - 3} more</p>
                  )}
                </div>
              )}
            </div>

            {/* Check Tracked Products */}
            <div className="bg-black border border-white/20 rounded-xl p-4">
              <h4 className="font-semibold flex items-center gap-2 mb-3">
                <Bell size={16} className="text-yellow-400" />
                Price Alerts
              </h4>
              <p className="text-xs text-gray-400 mb-3">Check deals for tracked products</p>
              <button
                onClick={checkTrackedProducts}
                disabled={isScanning || trackedProducts.length === 0}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-yellow-600/20 hover:bg-yellow-600/30 border border-yellow-500/30 rounded-lg text-yellow-400 text-sm transition-all disabled:opacity-50"
              >
                <RefreshCw size={16} className={isScanning ? 'animate-spin' : ''} />
                Check Now
              </button>
              <p className="text-xs text-gray-500 mt-2 text-center">
                {trackedProducts.length} product{trackedProducts.length !== 1 ? 's' : ''} tracked
              </p>
            </div>

            {/* Store Coverage */}
            <div className="bg-black border border-white/20 rounded-xl p-4">
              <h4 className="font-semibold flex items-center gap-2 mb-3">
                <Store size={16} className="text-purple-400" />
                Supported Stores
              </h4>
              <div className="flex flex-wrap gap-2">
                {scraperStores.map((store) => (
                  <div
                    key={store.id}
                    className="flex items-center gap-1 px-2 py-1 rounded-full text-xs"
                    style={{ backgroundColor: `${store.color}20`, color: store.color }}
                  >
                    {store.logo && <img src={store.logo} alt="" className="w-4 h-4 rounded bg-white" />}
                    {store.name}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Scraped Deals Grid */}
          {scrapedDeals.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold">{scrapedDeals.length} Deals Found</h4>
                <button
                  onClick={scanDeals}
                  disabled={isScanning}
                  className="flex items-center gap-2 text-sm text-purple-400 hover:text-purple-300"
                >
                  <RefreshCw size={14} className={isScanning ? 'animate-spin' : ''} />
                  Refresh
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {scrapedDeals.map((deal) => (
                  <div
                    key={deal.deal_id}
                    className={`bg-black border rounded-xl overflow-hidden transition-all ${
                      deal.saved ? 'border-green-500/50' : 'border-white/20 hover:border-purple-500/50'
                    }`}
                  >
                    {/* Product Image */}
                    {deal.image_url && (
                      <div className="relative h-40 bg-gray-900 overflow-hidden">
                        <img 
                          src={deal.image_url} 
                          alt={deal.product_name} 
                          className="w-full h-full object-contain p-2"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                    
                    <div className="p-4">
                      {/* Store & Deal Type */}
                      <div className="flex items-center gap-2 mb-2">
                        {deal.store_logo && (
                          <img src={deal.store_logo} alt={deal.store_name} className="w-5 h-5 rounded bg-white" />
                        )}
                        <span className="text-xs" style={{ color: deal.store_color }}>{deal.store_name}</span>
                        <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${
                          deal.deal_type === 'coupon' ? 'bg-yellow-500/20 text-yellow-400' :
                          deal.deal_type === 'clearance' ? 'bg-red-500/20 text-red-400' :
                          deal.deal_type === 'lightning' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-green-500/20 text-green-400'
                        }`}>
                          {deal.deal_type}
                        </span>
                      </div>

                      {/* Title */}
                      <h3 className="font-semibold text-sm mb-2 line-clamp-2">{deal.product_name}</h3>

                      {/* Price */}
                      <div className="flex items-center gap-2 mb-2">
                        {deal.sale_price && (
                          <span className="text-lg font-bold text-green-400">${deal.sale_price.toFixed(2)}</span>
                        )}
                        {deal.discount_percent && (
                          <span className="text-sm text-green-400">-{deal.discount_percent}% off</span>
                        )}
                      </div>

                      {/* Coupon Code */}
                      {deal.coupon_code && (
                        <div className="flex items-center gap-2 mb-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2">
                          <Tag size={14} className="text-yellow-400" />
                          <code className="text-yellow-400 font-mono text-sm">{deal.coupon_code}</code>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(deal.coupon_code);
                              toast.success('Code copied!');
                            }}
                            className="ml-auto p-1 hover:bg-yellow-500/20 rounded"
                          >
                            <Copy size={12} className="text-yellow-400" />
                          </button>
                        </div>
                      )}

                      {/* Category */}
                      {deal.category && deal.category !== 'other' && (
                        <span className="inline-block text-xs bg-white/10 text-gray-300 px-2 py-0.5 rounded mb-2">
                          {deal.category}
                        </span>
                      )}

                      {/* Actions */}
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/10">
                        <a
                          href={deal.product_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition-all"
                        >
                          <ExternalLink size={14} />
                          View
                        </a>
                        <button
                          onClick={() => setShowAddToListModal(deal)}
                          className="flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm transition-all"
                          title="Add to shopping list"
                        >
                          <ShoppingCart size={14} />
                          List
                        </button>
                        <button
                          onClick={() => saveScrapedDeal(deal)}
                          disabled={deal.saved}
                          className={`flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm transition-all ${
                            deal.saved
                              ? 'bg-green-500/20 text-green-400 cursor-default'
                              : 'bg-purple-600 hover:bg-purple-700'
                          }`}
                        >
                          {deal.saved ? <Check size={14} /> : <Save size={14} />}
                          {deal.saved ? 'Saved' : 'Save'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              <Radar size={48} className="mx-auto mb-4 opacity-50" />
              <p className="mb-2">No deals scanned yet</p>
              <p className="text-sm">Click "Scan All" to find deals across all stores</p>
            </div>
          )}
        </div>
      )}

      {/* Deals Section */}
      {activeSection === 'deals' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search deals..."
                className="w-full pl-10 pr-4 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none"
              />
            </div>
            <button
              onClick={() => setShowAddDealModal(true)}
              data-testid="add-deal-btn"
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-all ml-4"
            >
              <Plus size={16} />
              Add Deal
            </button>
          </div>

          {filteredDeals.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Tag size={48} className="mx-auto mb-4 opacity-50" />
              <p>No deals yet. Add a deal or use AI to find some!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredDeals.map((deal) => {
                const store = getStore(deal.store_id);
                const dealImage = deal.image_url || 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&h=300&fit=crop';
                return (
                  <div key={deal.id} className="bg-black border border-white/20 rounded-xl overflow-hidden hover:border-purple-500/50 transition-all group">
                    <div className="h-40 bg-gray-900 overflow-hidden relative">
                      <img 
                        src={dealImage} 
                        alt={deal.product_name} 
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&h=300&fit=crop';
                        }}
                      />
                      {!deal.image_url && (
                        <div className="absolute bottom-2 right-2 bg-black/70 text-xs text-gray-400 px-2 py-1 rounded">
                          No product image
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        {store.logo_url ? (
                          <img 
                            src={store.logo_url} 
                            alt={store.name} 
                            className="w-5 h-5 rounded bg-white object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling?.classList.remove('hidden');
                            }}
                          />
                        ) : null}
                        <span 
                          className={`w-5 h-5 rounded flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${store.logo_url ? 'hidden' : ''}`}
                          style={{ backgroundColor: store.color || '#8B5CF6' }}
                        >
                          {store.name?.charAt(0) || 'S'}
                        </span>
                        <span className="text-xs text-gray-400">{store.name}</span>
                        {deal.discount_percentage > 0 && (
                          <span className="ml-auto bg-green-500/20 text-green-400 text-xs px-2 py-0.5 rounded-full">
                            -{deal.discount_percentage}%
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-sm mb-2 line-clamp-2">{deal.product_name}</h3>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-lg font-bold text-green-400">${deal.sale_price?.toFixed(2)}</span>
                        {deal.original_price && (
                          <span className="text-sm text-gray-500 line-through">${deal.original_price.toFixed(2)}</span>
                        )}
                      </div>
                      {deal.expires_at && (
                        <div className="flex items-center gap-1 text-xs text-yellow-400 mb-3">
                          <Clock size={12} />
                          Expires: {new Date(deal.expires_at).toLocaleDateString()}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        {deal.product_url && (
                          <a
                            href={deal.product_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-xs font-medium transition-all"
                          >
                            <ExternalLink size={12} />
                            View Deal
                          </a>
                        )}
                        <button
                          onClick={() => handleDeleteDeal(deal.id)}
                          className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Shopping Lists Section */}
      {activeSection === 'lists' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Your Shopping Lists</h2>
            <button
              onClick={() => setShowAddListModal(true)}
              data-testid="add-list-btn"
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-all"
            >
              <Plus size={16} />
              New List
            </button>
          </div>

          {shoppingLists.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <ListChecks size={48} className="mx-auto mb-4 opacity-50" />
              <p>No shopping lists yet. Create one to get started!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {shoppingLists.map((list) => {
                const store = getStore(list.store_id);
                const progress = list.total_items > 0 ? (list.checked_items / list.total_items) * 100 : 0;
                const isOverBudget = list.budget && list.estimated_total > list.budget;
                const items = list.items || [];
                
                return (
                  <div 
                    key={list.id} 
                    className="bg-black border border-white/20 rounded-xl p-4 hover:border-white/40 transition-all flex flex-col gap-3"
                    data-testid={`shopping-list-card-${list.id}`}
                  >
                    {/* Card Header — click opens modal */}
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setShowListItemsModal(list.id)}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {store.logo_url && (
                          <img src={store.logo_url} alt={store.name} className="w-5 h-5 rounded bg-white flex-shrink-0" />
                        )}
                        <h3 className="font-semibold truncate">{list.name}</h3>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                        <span className="text-xs text-gray-500">{list.checked_items || 0}/{list.total_items || 0}</span>
                        <button
                          onClick={(e) => handleShareList(list.id, e)}
                          className="p-1 text-gray-400 hover:text-blue-400 transition-all"
                          title="Copy share link"
                          data-testid={`share-list-${list.id}`}
                        >
                          <Share2 size={13} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteList(list.id); }}
                          className="p-1 text-gray-400 hover:text-red-400 transition-all"
                          data-testid={`delete-list-${list.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="w-full h-1.5 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-purple-600 to-pink-600 transition-all"
                        style={{ width: `${progress}%` }}
                      />
                    </div>

                    {/* Items scroller — 6 rows visible, scrolls for more */}
                    {items.length > 0 ? (
                      <div
                        className="overflow-y-auto scrollbar-hide"
                        style={{ maxHeight: '168px' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="space-y-0.5">
                          {items.map((item) => (
                            <div
                              key={item.id}
                              className="flex items-center gap-2 px-1 py-1 rounded-lg hover:bg-white/5 group/item"
                              style={{ minHeight: '28px' }}
                            >
                              {/* Checkbox */}
                              <button
                                onClick={(e) => { e.stopPropagation(); handleToggleItem(list.id, item.id, item.is_checked); }}
                                className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-all ${
                                  item.is_checked
                                    ? 'bg-purple-600 border-purple-600'
                                    : 'border-white/30 hover:border-purple-400'
                                }`}
                                data-testid={`toggle-item-${item.id}`}
                              >
                                {item.is_checked && <Check size={10} className="text-white" />}
                              </button>

                              {/* Name */}
                              <span className={`text-sm flex-1 truncate ${item.is_checked ? 'line-through text-gray-600' : 'text-gray-200'}`}>
                                {item.name}
                              </span>

                              {/* Qty & price */}
                              <span className="text-xs text-gray-500 flex-shrink-0">
                                {item.quantity > 1 && `×${item.quantity}`}
                                {item.price ? ` $${(item.price * (item.quantity || 1)).toFixed(0)}` : ''}
                              </span>

                              {/* External link */}
                              {item.product_url && (
                                <a
                                  href={item.product_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="flex-shrink-0 opacity-0 group-hover/item:opacity-100 transition-opacity"
                                  data-testid={`item-link-${item.id}`}
                                >
                                  <ExternalLink size={11} className="text-gray-500 hover:text-white" />
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowListItemsModal(list.id)}
                        className="text-xs text-gray-600 hover:text-gray-400 text-center py-3 border border-dashed border-white/10 rounded-lg transition-all"
                      >
                        + Add items
                      </button>
                    )}

                    {/* Footer: total + budget */}
                    <div className="flex items-center justify-between text-xs pt-1 border-t border-white/5">
                      <span className="text-gray-500">Est. Total</span>
                      <div className="flex items-center gap-1">
                        <span className={`font-semibold ${isOverBudget ? 'text-red-400' : 'text-white'}`}>
                          ${list.estimated_total?.toFixed(2) || '0.00'}
                        </span>
                        {list.budget && (
                          <span className="text-gray-600">/ ${list.budget}</span>
                        )}
                        {isOverBudget && <AlertCircle size={11} className="text-red-400 ml-1" />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Coupons Section */}
      {activeSection === 'coupons' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Saved Coupons</h2>
            <button
              onClick={() => setShowAddCouponModal(true)}
              data-testid="add-coupon-btn"
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-all"
            >
              <Plus size={16} />
              Add Coupon
            </button>
          </div>

          {filteredCoupons.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Percent size={48} className="mx-auto mb-4 opacity-50" />
              <p>No coupons saved. Add promo codes to use later!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredCoupons.map((coupon) => {
                const store = getStore(coupon.store_id);
                const isExpired = coupon.expires_at && new Date(coupon.expires_at) < new Date();
                
                return (
                  <div key={coupon.id} className={`bg-black border rounded-xl p-4 ${isExpired ? 'border-red-500/30 opacity-60' : 'border-white/20 hover:border-yellow-500/50'} transition-all`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {store.logo_url && <img src={store.logo_url} alt={store.name} className="w-5 h-5 rounded bg-white" />}
                        <span className="text-sm text-gray-400">{store.name}</span>
                      </div>
                      {isExpired && <span className="text-xs text-red-400">Expired</span>}
                    </div>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <code className="flex-1 bg-yellow-500/10 text-yellow-400 px-3 py-2 rounded-lg font-mono text-lg">
                        {coupon.code}
                      </code>
                      <button
                        onClick={() => copyCouponCode(coupon.code)}
                        className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all"
                        title="Copy code"
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                    
                    {coupon.description && (
                      <p className="text-sm text-gray-400 mb-2">{coupon.description}</p>
                    )}
                    
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>
                        {coupon.discount_type === 'percentage' && coupon.discount_value && `${coupon.discount_value}% off`}
                        {coupon.discount_type === 'fixed' && coupon.discount_value && `$${coupon.discount_value} off`}
                        {coupon.discount_type === 'free_shipping' && 'Free Shipping'}
                      </span>
                      {coupon.min_purchase && <span>Min. ${coupon.min_purchase}</span>}
                    </div>
                    
                    {coupon.expires_at && !isExpired && (
                      <div className="flex items-center gap-1 text-xs text-yellow-400 mt-2">
                        <Clock size={12} />
                        Expires: {new Date(coupon.expires_at).toLocaleDateString()}
                      </div>
                    )}
                    
                    <button
                      onClick={() => handleDeleteCoupon(coupon.id)}
                      className="w-full mt-3 p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg text-xs transition-all"
                    >
                      Remove Coupon
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* AI Suggestions Section */}
      {activeSection === 'ai' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Sparkles className="text-purple-400" />
                AI-Powered Deal Suggestions
              </h2>
              <p className="text-sm text-gray-400">Curated deals based on popularity and savings</p>
            </div>
            <button
              onClick={findAiDeals}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-lg text-sm font-medium transition-all"
            >
              <Sparkles size={16} />
              Refresh Deals
            </button>
          </div>

          {aiDeals.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Sparkles size={48} className="mx-auto mb-4 opacity-50" />
              <p>Click "Find AI Deals" to discover hot deals!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {aiDeals.map((deal) => (
                <div key={deal.id} className="bg-black border border-purple-500/30 rounded-xl overflow-hidden hover:border-purple-500 transition-all group">
                  {deal.image_url && (
                    <div className="h-40 bg-gray-900 overflow-hidden relative">
                      <img src={deal.image_url} alt={deal.product_name} className="w-full h-full object-contain group-hover:scale-105 transition-transform" />
                      <div className="absolute top-2 right-2 bg-purple-600 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <Sparkles size={10} />
                        AI Score: {deal.ai_score}
                      </div>
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-gray-400">{deal.store_name}</span>
                      {deal.discount_percentage > 0 && (
                        <span className="ml-auto bg-green-500/20 text-green-400 text-xs px-2 py-0.5 rounded-full">
                          -{deal.discount_percentage}%
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-sm mb-2 line-clamp-2">{deal.product_name}</h3>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg font-bold text-green-400">${deal.sale_price?.toFixed(2)}</span>
                      {deal.original_price && (
                        <span className="text-sm text-gray-500 line-through">${deal.original_price.toFixed(2)}</span>
                      )}
                    </div>
                    {deal.ai_reason && (
                      <p className="text-xs text-purple-300 mb-3 italic">"{deal.ai_reason}"</p>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => saveAiDeal(deal)}
                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-xs font-medium transition-all"
                      >
                        <Plus size={12} />
                        Save Deal
                      </button>
                      {deal.product_url && (
                        <a
                          href={deal.product_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all"
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Deal Modal */}
      {showAddDealModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowAddDealModal(false)}>
          <div className="bg-gray-900 border border-white/20 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2 className="text-lg font-semibold">Add Deal</h2>
              <button onClick={() => setShowAddDealModal(false)} className="p-1 hover:bg-white/10 rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Store *</label>
                <select value={newDeal.store_id} onChange={e => setNewDeal({...newDeal, store_id: e.target.value})} className="w-full px-4 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none">
                  <option value="">Select store...</option>
                  {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Product Name *</label>
                <input type="text" value={newDeal.product_name} onChange={e => setNewDeal({...newDeal, product_name: e.target.value})} placeholder="e.g., iPhone 15 Pro" className="w-full px-4 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Original Price</label>
                  <input type="number" value={newDeal.original_price} onChange={e => setNewDeal({...newDeal, original_price: e.target.value})} placeholder="99.99" className="w-full px-4 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Sale Price *</label>
                  <input type="number" value={newDeal.sale_price} onChange={e => setNewDeal({...newDeal, sale_price: e.target.value})} placeholder="79.99" className="w-full px-4 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Product URL</label>
                <input type="url" value={newDeal.product_url} onChange={e => setNewDeal({...newDeal, product_url: e.target.value})} placeholder="https://..." className="w-full px-4 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Image URL</label>
                <input type="url" value={newDeal.image_url} onChange={e => setNewDeal({...newDeal, image_url: e.target.value})} placeholder="https://..." className="w-full px-4 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Expires</label>
                <input type="date" value={newDeal.expires_at} onChange={e => setNewDeal({...newDeal, expires_at: e.target.value})} className="w-full px-4 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none" />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-white/10">
              <button onClick={() => setShowAddDealModal(false)} className="px-4 py-2 bg-black border border-white/20 hover:border-white/40 rounded-lg text-sm">Cancel</button>
              <button onClick={handleAddDeal} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium">Add Deal</button>
            </div>
          </div>
        </div>
      )}

      {/* Add List Modal */}
      {showAddListModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowAddListModal(false)}>
          <div className="bg-gray-900 border border-white/20 rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2 className="text-lg font-semibold">New Shopping List</h2>
              <button onClick={() => setShowAddListModal(false)} className="p-1 hover:bg-white/10 rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">List Name *</label>
                <input type="text" value={newList.name} onChange={e => setNewList({...newList, name: e.target.value})} placeholder="e.g., Weekly Groceries" className="w-full px-4 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Store (optional)</label>
                <select value={newList.store_id} onChange={e => setNewList({...newList, store_id: e.target.value})} className="w-full px-4 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none">
                  <option value="">Any store</option>
                  {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Budget (optional)</label>
                <input type="number" value={newList.budget} onChange={e => setNewList({...newList, budget: e.target.value})} placeholder="100.00" className="w-full px-4 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={newList.is_advanced} onChange={e => setNewList({...newList, is_advanced: e.target.checked})} className="w-4 h-4 rounded border-white/20 bg-black text-purple-600 focus:ring-purple-500" />
                <span className="text-sm">Advanced list (with prices & quantities)</span>
              </label>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-white/10">
              <button onClick={() => setShowAddListModal(false)} className="px-4 py-2 bg-black border border-white/20 hover:border-white/40 rounded-lg text-sm">Cancel</button>
              <button onClick={handleAddList} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium">Create List</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Coupon Modal */}
      {showAddCouponModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowAddCouponModal(false)}>
          <div className="bg-gray-900 border border-white/20 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2 className="text-lg font-semibold">Add Coupon</h2>
              <button onClick={() => setShowAddCouponModal(false)} className="p-1 hover:bg-white/10 rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Store *</label>
                <select value={newCoupon.store_id} onChange={e => setNewCoupon({...newCoupon, store_id: e.target.value})} className="w-full px-4 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none">
                  <option value="">Select store...</option>
                  {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Coupon Code *</label>
                <input type="text" value={newCoupon.code} onChange={e => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})} placeholder="SAVE20" className="w-full px-4 py-2 bg-black border border-white/20 rounded-lg text-white font-mono focus:border-purple-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <input type="text" value={newCoupon.description} onChange={e => setNewCoupon({...newCoupon, description: e.target.value})} placeholder="20% off entire order" className="w-full px-4 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Discount Type</label>
                  <select value={newCoupon.discount_type} onChange={e => setNewCoupon({...newCoupon, discount_type: e.target.value})} className="w-full px-4 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none">
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                    <option value="free_shipping">Free Shipping</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Discount Value</label>
                  <input type="number" value={newCoupon.discount_value} onChange={e => setNewCoupon({...newCoupon, discount_value: e.target.value})} placeholder="20" className="w-full px-4 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Min. Purchase</label>
                  <input type="number" value={newCoupon.min_purchase} onChange={e => setNewCoupon({...newCoupon, min_purchase: e.target.value})} placeholder="50" className="w-full px-4 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Expires</label>
                  <input type="date" value={newCoupon.expires_at} onChange={e => setNewCoupon({...newCoupon, expires_at: e.target.value})} className="w-full px-4 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-white/10">
              <button onClick={() => setShowAddCouponModal(false)} className="px-4 py-2 bg-black border border-white/20 hover:border-white/40 rounded-lg text-sm">Cancel</button>
              <button onClick={handleAddCoupon} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium">Save Coupon</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Store Modal */}
      {showAddStoreModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowAddStoreModal(false)}>
          <div className="bg-gray-900 border border-white/20 rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2 className="text-lg font-semibold">Add Store</h2>
              <button onClick={() => setShowAddStoreModal(false)} className="p-1 hover:bg-white/10 rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-4 space-y-4">
              {/* Logo Preview */}
              <div className="flex items-center gap-4 p-4 bg-black/50 rounded-xl border border-white/10">
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center overflow-hidden"
                  style={{ backgroundColor: newStore.color || '#8B5CF6' }}
                >
                  {(newStore.website_url || newStore.name) ? (
                    <img 
                      src={`https://logo.clearbit.com/${newStore.website_url?.replace(/https?:\/\//, '').replace(/\/.*/g, '') || newStore.name.toLowerCase().replace(/\s+/g, '') + '.com'}`}
                      alt="Logo preview"
                      className="w-12 h-12 object-contain bg-white rounded-lg p-1"
                      onError={(e) => { 
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center" style={{ display: (newStore.website_url || newStore.name) ? 'none' : 'flex' }}>
                    <Store size={24} className="text-white/60" />
                  </div>
                </div>
                <div>
                  <p className="font-medium text-white">{newStore.name || 'Store Name'}</p>
                  <p className="text-sm text-gray-400">{newStore.website_url || 'https://example.com'}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Store Name *</label>
                <input 
                  type="text" 
                  value={newStore.name} 
                  onChange={e => setNewStore({...newStore, name: e.target.value})} 
                  placeholder="e.g., Costco, Macy's, Nike" 
                  className="w-full px-4 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                  data-testid="store-name-input"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Website URL (for logo)</label>
                <input 
                  type="url" 
                  value={newStore.website_url} 
                  onChange={e => setNewStore({...newStore, website_url: e.target.value})} 
                  placeholder="https://costco.com" 
                  className="w-full px-4 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                  data-testid="store-url-input"
                />
                <p className="text-xs text-gray-500 mt-1">Logo will be automatically fetched from the website</p>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Brand Color</label>
                <div className="flex items-center gap-3">
                  <input 
                    type="color" 
                    value={newStore.color} 
                    onChange={e => setNewStore({...newStore, color: e.target.value})} 
                    className="w-12 h-10 bg-black border border-white/20 rounded-lg cursor-pointer"
                  />
                  <div className="flex gap-2">
                    {['#FF9900', '#0071DC', '#CC0000', '#0046BE', '#E31837', '#F96302', '#8B5CF6', '#10B981'].map(color => (
                      <button
                        key={color}
                        onClick={() => setNewStore({...newStore, color})}
                        className={`w-8 h-8 rounded-lg transition-all ${newStore.color === color ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900' : ''}`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-white/10">
              <button onClick={() => setShowAddStoreModal(false)} className="px-4 py-2 bg-black border border-white/20 hover:border-white/40 rounded-lg text-sm">Cancel</button>
              <button 
                onClick={handleAddStore} 
                disabled={!newStore.name}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-600/50 disabled:cursor-not-allowed rounded-lg text-sm font-medium"
                data-testid="store-submit-btn"
              >
                Add Store
              </button>
            </div>
          </div>
        </div>
      )}

      {/* List Items Modal */}
      {showListItemsModal && activeList && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowListItemsModal(null)}>
          <div className="bg-gray-900 border border-white/20 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div>
                <h2 className="text-lg font-semibold">{activeList.name}</h2>
                <p className="text-sm text-gray-400">{(activeList.items || []).length} items</p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={(e) => handleShareList(activeList.id, e)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600/40 border border-blue-500/30 text-blue-400 rounded-lg text-xs font-medium transition-all"
                  title="Copy share link"
                  data-testid="modal-share-list-btn"
                >
                  <Share2 size={13} />
                  Share
                </button>
                <button onClick={() => setShowListItemsModal(null)} className="p-1 hover:bg-white/10 rounded-lg ml-1"><X size={20} /></button>
              </div>
            </div>
            
            {/* Add Item Form */}
            <div className="p-4 border-b border-white/10 bg-black/50 space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newListItem.name}
                  onChange={e => setNewListItem({...newListItem, name: e.target.value})}
                  placeholder="Item name..."
                  className="flex-1 px-3 py-2 bg-black border border-white/20 rounded-lg text-white text-sm focus:border-purple-500 focus:outline-none"
                  onKeyPress={e => e.key === 'Enter' && handleAddListItem(activeList.id)}
                  data-testid="new-item-name"
                />
                <input
                  type="number"
                  value={newListItem.quantity}
                  onChange={e => setNewListItem({...newListItem, quantity: e.target.value})}
                  placeholder="Qty"
                  className="w-16 px-2 py-2 bg-black border border-white/20 rounded-lg text-white text-sm focus:border-purple-500 focus:outline-none"
                  data-testid="new-item-qty"
                />
                <input
                  type="number"
                  value={newListItem.price}
                  onChange={e => setNewListItem({...newListItem, price: e.target.value})}
                  placeholder="$"
                  className="w-20 px-2 py-2 bg-black border border-white/20 rounded-lg text-white text-sm focus:border-purple-500 focus:outline-none"
                  data-testid="new-item-price"
                />
                <button
                  onClick={() => handleAddListItem(activeList.id)}
                  className="p-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-all"
                  data-testid="add-item-btn"
                >
                  <Plus size={18} />
                </button>
              </div>
              <input
                type="url"
                value={newListItem.product_url}
                onChange={e => setNewListItem({...newListItem, product_url: e.target.value})}
                placeholder="Product URL (optional) — https://..."
                className="w-full px-3 py-2 bg-black border border-white/20 rounded-lg text-white text-sm focus:border-purple-500 focus:outline-none"
                data-testid="new-item-url"
              />
            </div>
            
            {/* Items List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {(activeList.items || []).length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <ShoppingBag size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No items yet</p>
                </div>
              ) : (
                (activeList.items || []).map(item => (
                  <div key={item.id} className={`rounded-lg bg-black/50 border border-white/5 transition-all ${item.is_checked ? 'opacity-50' : ''}`}>
                    {editingItemId === item.id ? (
                      /* ── EDIT MODE ── */
                      <div className="p-3 space-y-2">
                        <input
                          type="text"
                          value={editingItemData.name ?? item.name}
                          onChange={e => setEditingItemData(d => ({...d, name: e.target.value}))}
                          className="w-full px-3 py-1.5 bg-gray-800 border border-purple-500 rounded-lg text-white text-sm focus:outline-none"
                          data-testid={`edit-name-${item.id}`}
                        />
                        <div className="flex gap-2">
                          <input
                            type="number"
                            value={editingItemData.quantity ?? item.quantity}
                            onChange={e => setEditingItemData(d => ({...d, quantity: e.target.value}))}
                            placeholder="Qty"
                            className="w-20 px-2 py-1.5 bg-gray-800 border border-white/20 rounded-lg text-white text-sm focus:outline-none"
                          />
                          <input
                            type="number"
                            value={editingItemData.price ?? item.price ?? ''}
                            onChange={e => setEditingItemData(d => ({...d, price: e.target.value}))}
                            placeholder="Price"
                            className="w-24 px-2 py-1.5 bg-gray-800 border border-white/20 rounded-lg text-white text-sm focus:outline-none"
                          />
                        </div>
                        <input
                          type="url"
                          value={editingItemData.product_url ?? (item.product_url || '')}
                          onChange={e => setEditingItemData(d => ({...d, product_url: e.target.value}))}
                          placeholder="Product URL..."
                          className="w-full px-3 py-1.5 bg-gray-800 border border-white/20 rounded-lg text-white text-sm focus:outline-none"
                          data-testid={`edit-url-${item.id}`}
                        />
                        <input
                          type="text"
                          value={editingItemData.notes ?? (item.notes || '')}
                          onChange={e => setEditingItemData(d => ({...d, notes: e.target.value}))}
                          placeholder="Notes..."
                          className="w-full px-3 py-1.5 bg-gray-800 border border-white/20 rounded-lg text-white text-sm focus:outline-none"
                        />
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => { setEditingItemId(null); setEditingItemData({}); }}
                            className="px-3 py-1.5 text-xs text-gray-400 hover:text-white border border-white/20 rounded-lg"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleEditListItem(activeList.id, item.id)}
                            className="px-3 py-1.5 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-1"
                            data-testid={`save-item-${item.id}`}
                          >
                            <Check size={12} /> Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* ── VIEW MODE ── */
                      <div className="flex items-center gap-3 p-3">
                        <button
                          onClick={() => handleToggleItem(activeList.id, item.id, item.is_checked)}
                          className={`w-5 h-5 rounded border flex items-center justify-center transition-all flex-shrink-0 ${item.is_checked ? 'bg-green-500 border-green-500' : 'border-white/30 hover:border-purple-500'}`}
                          data-testid={`toggle-item-${item.id}`}
                        >
                          {item.is_checked && <Check size={12} />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${item.is_checked ? 'line-through text-gray-500' : 'text-white'}`}>
                              {item.name}
                            </span>
                            {(item.product_url) && (
                              <a
                                href={item.product_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-purple-400 hover:text-purple-300 transition-colors"
                                title="View item"
                                data-testid={`item-link-${item.id}`}
                                onClick={e => e.stopPropagation()}
                              >
                                <ExternalLink size={13} />
                              </a>
                            )}
                          </div>
                          {item.notes && <p className="text-xs text-gray-500 mt-0.5">{item.notes}</p>}
                          {(item.quantity > 1 || item.price) && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              {item.quantity > 1 && `x${item.quantity}`}
                              {item.price && ` · $${(item.price * (item.quantity || 1)).toFixed(2)}`}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => { setEditingItemId(item.id); setEditingItemData({}); }}
                            className="p-1.5 text-gray-500 hover:text-blue-400 transition-colors rounded"
                            title="Edit"
                            data-testid={`edit-item-${item.id}`}
                          >
                            <Edit size={13} />
                          </button>
                          <button
                            onClick={() => handleDeleteListItem(activeList.id, item.id)}
                            className="p-1.5 text-gray-500 hover:text-red-400 transition-colors rounded"
                            title="Delete"
                            data-testid={`delete-item-${item.id}`}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
            
            {/* Footer */}
            {activeList.items?.some(i => i.price) && (
              <div className="p-4 border-t border-white/10 bg-black/50">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Estimated Total:</span>
                  <span className="text-xl font-bold text-green-400">
                    ${(activeList.items || []).reduce((s, i) => s + ((i.price || 0) * (i.quantity || 1)), 0).toFixed(2)}
                  </span>
                </div>
                {activeList.budget && (
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-gray-500">Budget:</span>
                    <span className={(activeList.items || []).reduce((s, i) => s + ((i.price || 0) * (i.quantity || 1)), 0) > activeList.budget ? 'text-red-400' : 'text-gray-400'}>
                      ${activeList.budget.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Track Product Modal */}
      {showTrackModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-white/20 rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Target className="text-blue-400" />
                Track Product
              </h2>
              <button onClick={() => setShowTrackModal(false)} className="p-1 hover:bg-white/10 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Product Name *</label>
                <input
                  type="text"
                  value={newTrack.product_name}
                  onChange={e => setNewTrack({...newTrack, product_name: e.target.value})}
                  placeholder="e.g., AirPods Pro, Nike Air Max"
                  className="w-full px-3 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Target Price (optional)</label>
                <div className="relative">
                  <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="number"
                    value={newTrack.target_price}
                    onChange={e => setNewTrack({...newTrack, target_price: e.target.value})}
                    placeholder="Alert when price drops below"
                    className="w-full pl-10 pr-4 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Keywords (comma separated)</label>
                <input
                  type="text"
                  value={newTrack.keywords}
                  onChange={e => setNewTrack({...newTrack, keywords: e.target.value})}
                  placeholder="e.g., wireless, noise cancelling"
                  className="w-full px-3 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Stores to Monitor</label>
                <div className="flex flex-wrap gap-2">
                  {scraperStores.map((store) => (
                    <button
                      key={store.id}
                      onClick={() => {
                        const currentStores = newTrack.stores || [];
                        if (currentStores.includes(store.id)) {
                          setNewTrack({...newTrack, stores: currentStores.filter(s => s !== store.id)});
                        } else {
                          setNewTrack({...newTrack, stores: [...currentStores, store.id]});
                        }
                      }}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-all ${
                        newTrack.stores?.includes(store.id)
                          ? 'bg-purple-600 text-white'
                          : 'bg-black border border-white/20 hover:border-white/40'
                      }`}
                    >
                      {store.logo && <img src={store.logo} alt="" className="w-4 h-4 rounded bg-white" />}
                      {store.name}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">Leave empty to track across all stores</p>
              </div>
            </div>
            <div className="flex gap-3 p-4 border-t border-white/10">
              <button
                onClick={() => setShowTrackModal(false)}
                className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleTrackProduct}
                data-testid="confirm-track-product-btn"
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-all"
              >
                Start Tracking
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add to Shopping List Modal */}
      {showAddToListModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowAddToListModal(null)}>
          <div className="bg-gray-900 border border-white/20 rounded-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <ShoppingCart className="text-blue-400" />
                Add to Shopping List
              </h2>
              <button onClick={() => setShowAddToListModal(null)} className="p-1 hover:bg-white/10 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            {/* Product Preview */}
            <div className="p-4 border-b border-white/10">
              <div className="flex items-start gap-3">
                {showAddToListModal.image_url && (
                  <img 
                    src={showAddToListModal.image_url} 
                    alt="" 
                    className="w-16 h-16 rounded-lg object-contain bg-white/5"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-white text-sm line-clamp-2">{showAddToListModal.product_name}</h3>
                  {showAddToListModal.sale_price && (
                    <p className="text-green-400 font-bold mt-1">${showAddToListModal.sale_price.toFixed(2)}</p>
                  )}
                  <p className="text-xs text-gray-400">{showAddToListModal.store_name || showAddToListModal.store}</p>
                </div>
              </div>
            </div>
            
            <div className="p-4 space-y-4">
              {shoppingLists.length === 0 ? (
                <div className="text-center py-4 text-gray-400">
                  <ShoppingBag size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No shopping lists yet</p>
                  <button
                    onClick={() => {
                      setShowAddToListModal(null);
                      setShowAddListModal(true);
                    }}
                    className="mt-3 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium"
                  >
                    Create a List
                  </button>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Select a list:</label>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {shoppingLists.map(list => (
                        <button
                          key={list.id}
                          onClick={() => setSelectedListId(list.id)}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all ${
                            selectedListId === list.id
                              ? 'bg-purple-600/20 border-purple-500'
                              : 'bg-white/5 border-white/10 hover:border-white/30'
                          }`}
                        >
                          <ListChecks size={18} className={selectedListId === list.id ? 'text-purple-400' : 'text-gray-400'} />
                          <div className="flex-1 text-left">
                            <p className="font-medium text-white">{list.name}</p>
                            <p className="text-xs text-gray-400">{list.items?.length || 0} items</p>
                          </div>
                          {selectedListId === list.id && <Check size={18} className="text-purple-400" />}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowAddToListModal(null)}
                      className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg font-medium transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => addDealToList(showAddToListModal, selectedListId)}
                      disabled={!selectedListId}
                      className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 disabled:cursor-not-allowed rounded-lg font-medium transition-all"
                      data-testid="confirm-add-to-list-btn"
                    >
                      Add to List
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shopping;
